import { useTracks, VideoTrack, useLocalParticipant } from '@livekit/components-react';
import { Track } from 'livekit-client';

// ---------------------------------------------------------------------------
// ScreenShareView — renders when any participant is sharing their screen.
// Returns null (takes up no space) when no screen share is active.
// When active, it expands to fill the main area and pushes the camera grid
// into a secondary position (handled via CSS class on the parent).
//
// The local participant's own screen share is intentionally NOT rendered here.
// Showing your own share back to yourself creates an infinite mirror effect
// because the browser's getDisplayMedia captures the rendered pixels of this
// very page. Other remote participants still see the share normally.
// ---------------------------------------------------------------------------
export default function ScreenShareView() {
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks(
    [{ source: Track.Source.ScreenShare, withPlaceholder: false }],
  );

  // Exclude local participant's own share to avoid infinite mirror effect
  const remoteTracks = tracks.filter((t) => t.participant.identity !== localParticipant?.identity);

  if (remoteTracks.length === 0) {
    // If the local user is sharing but no remote share exists, show a notice
    const localIsSharing = tracks.length > 0;
    if (localIsSharing) {
      return (
        <div className="lk-screenshare-view lk-screenshare-view--self">
          <div className="lk-screenshare-self-notice">
            <span aria-hidden="true">🖥</span>
            <p>You are sharing your screen</p>
            <p className="lk-screenshare-self-sub">Others can see your screen. It is hidden here to avoid an infinite mirror.</p>
          </div>
        </div>
      );
    }
    return null;
  }

  // If multiple people share, show the first remote one
  const trackRef = remoteTracks[0];
  const sharerName = trackRef.participant?.name || trackRef.participant?.identity || 'Someone';

  return (
    <div className="lk-screenshare-view">
      <VideoTrack trackRef={trackRef} className="lk-screenshare-video" />
      <div className="lk-screenshare-badge">
        <span className="lk-screenshare-icon" aria-hidden="true">🖥</span>
        {sharerName} is sharing their screen
      </div>
    </div>
  );
}
